/**
 * Frame: contenitore standard per ogni schermata.
 * - StatusBar in alto
 * - Header con titolo e crumb
 * - Body scrollabile
 * - Footer con bottoni (lo decide la screen)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getVersionShort } from '../lib/appVersion';
import TorBadge from './TorBadge';

export function Frame({ children, statusLeft, statusRight }) {
  const [ver, setVer] = useState(typeof __APP_VERSION__ !== 'undefined' ? `v${__APP_VERSION__}` : 'dev');
  useEffect(() => { getVersionShort().then(setVer).catch(() => {}); }, []);
  return (
    // 100dvh (dynamic viewport): su mobile il vecchio 100vh include l'area
    // dietro la barra del browser, spingendo il footer sotto la piega.
    // safe-area-inset-* tiene conto di notch e barra home.
    <div className="min-h-[100dvh] bg-black text-ink font-mono flex flex-col">
      <div
        className="flex-1 w-full max-w-md mx-auto flex flex-col px-4 pt-5"
        style={{
          // Margine sotto generoso: oltre alla safe-area (notch/barra home),
          // dà spazio perché il pulsante del footer NON finisca sotto la barra
          // in basso del browser (soprattutto nei browser in-app tipo WhatsApp/IG).
          paddingBottom: 'max(1.75rem, calc(env(safe-area-inset-bottom) + 1rem))',
        }}
      >
        <div className="flex justify-between items-center text-[11px] text-ink-dim tracking-widest border-b border-ink-faint pb-1.5 mb-3">
          <span>{statusLeft}</span>
          <span className="flex items-center gap-2">
            <TorBadge />
            {statusRight}
            <span
              className="text-ink-dim normal-case tracking-normal text-[12px]"
              title={typeof __BUILD_ID__ !== 'undefined' ? `build ${__BUILD_ID__}` : 'build'}
            >
              {ver}
            </span>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Header({ title, crumb, onCrumbClick, amber = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // "Home" ovunque tranne sull'inbox e sul boot.
  const showHome = pathname !== '/inbox' && pathname !== '/';
  return (
    <div className="flex justify-between items-center border-b border-ink-faint pb-2 mb-4 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {crumb && (onCrumbClick ? (
          <button
            type="button"
            onClick={onCrumbClick}
            className="shrink-0 border border-ink-dim/60 rounded-lg text-ink-dim hover:text-ink hover:border-ink text-[13px] px-2.5 py-1.5 transition-colors"
          >
            {crumb}
          </button>
        ) : (
          <span className="shrink-0 text-[13px] text-ink-dim">{crumb}</span>
        ))}
        <span className={`text-lg tracking-wide truncate ${amber ? 'text-amber text-glow-amber' : 'text-ink text-glow'}`}>
          {title}
        </span>
      </div>
      {showHome && (
        <button
          type="button"
          onClick={() => navigate('/inbox')}
          title="Home (inbox)"
          aria-label="Home"
          className="shrink-0 border border-ink-dim/60 rounded-lg text-ink-dim hover:text-ink hover:border-ink text-[16px] px-2.5 py-1.5 transition-colors"
        >
          🏠
        </button>
      )}
    </div>
  );
}

export function Footer({ children }) {
  return <div className="mt-auto pt-4 space-y-2">{children}</div>;
}

export function Box({ children, variant = 'default', className = '' }) {
  const v = {
    default: 'border-ink-dim',
    warn: 'border-amber',
    danger: 'border-red-500',
    success: 'border-ink',
  }[variant] || 'border-ink-dim';
  return (
    <div className={`border ${v} rounded-xl p-3 ${className}`}>{children}</div>
  );
}

export function Btn({ children, variant = 'default', onClick, disabled, type = 'button', className = '' }) {
  const v = {
    default: 'border-ink text-ink hover:bg-ink hover:text-black',
    primary: 'bg-ink text-black border-ink hover:bg-ink-dim',
    amber: 'border-amber text-amber hover:bg-amber hover:text-black',
    ghost: 'border-ink-faint text-ink-dim hover:border-ink-dim hover:text-ink',
    danger: 'border-redphos text-redphos hover:bg-redphos hover:text-black',
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full border ${v} rounded-lg font-mono text-[15px] py-3 px-4 tracking-wide text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

export function FieldLabel({ children }) {
  return (
    <label className="block text-[15px] uppercase tracking-widest text-ink-dim mb-1.5">
      &gt; {children}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-black border border-ink-dim rounded-lg text-ink font-mono text-[16px] py-2.5 px-3 outline-none focus:border-ink ${props.className || ''}`}
    />
  );
}

export function Cursor() {
  return <span className="inline-block w-2 h-3 bg-ink align-text-bottom ml-1 animate-blink shadow-glow" />;
}
